import { useMemo } from "react";
import { padded } from "@/api/adapt";
import { idx } from "@/lib/months";
import { previewCurve, previewRange } from "@/lib/preview";
import { sameValues, savedSeriesFor } from "@/model/store";
import type { EventNode, MetricNode } from "@/model/types";
import { useNodeDetail } from "./world";

/**
 * The curve to draw through an event's points: the API's exact series for the saved baseline or for the
 * scenario's own curve (`scenario_series`), or a straight-line preview while points are being dragged (or
 * until the saved series arrives).
 */
export function useEventStated(
	node: EventNode,
	shown: Record<string, number>,
	dragging: boolean,
	scenarioId: string | null = null,
): { F: number[]; exact: boolean } {
	const detail = useNodeDetail(node.id, "year", scenarioId);
	return useMemo(() => {
		if (!dragging) {
			const saved = savedSeriesFor(node.id, shown)?.baseline_series;
			if (saved?.kind === "event") return { F: padded(saved.chance_by), exact: true };
			const d = detail.data;
			if (d?.scenario_series?.kind === "event" && sameValues(d.scenario_values?.by ?? {}, shown))
				return { F: padded(d.scenario_series.chance_by), exact: true };
			if (d?.baseline_series?.kind === "event" && sameValues(d.node.forecast.by ?? {}, shown)) return { F: padded(d.baseline_series.chance_by), exact: true };
		}
		return { F: previewCurve(shown), exact: false };
	}, [node.id, shown, dragging, detail.data]);
}

/** The same for a metric's low / likely / high bands. */
export function useMetricStated(
	node: MetricNode,
	shown: Record<string, [number, number, number]>,
	dragging: boolean,
	scenarioId: string | null = null,
): { lo: number[]; mid: number[]; hi: number[]; exact: boolean } | null {
	const detail = useNodeDetail(node.id, "year", scenarioId);
	return useMemo(() => {
		if (node.formula) return null;
		if (!dragging) {
			const saved = savedSeriesFor(node.id, shown)?.baseline_series;
			if (saved?.kind === "metric") return { lo: padded(saved.low), mid: padded(saved.likely), hi: padded(saved.high), exact: true };
			const d = detail.data;
			const own = d?.scenario_series;
			if (own?.kind === "metric" && sameValues(d?.scenario_values?.range ?? {}, shown))
				return { lo: padded(own.low), mid: padded(own.likely), hi: padded(own.high), exact: true };
			if (d?.baseline_series?.kind === "metric" && sameValues(d.node.forecast.range ?? {}, shown))
				return { lo: padded(d.baseline_series.low), mid: padded(d.baseline_series.likely), hi: padded(d.baseline_series.high), exact: true };
		}
		const past = node.observed.filter((o) => idx(o.month) < 0);
		return { ...previewRange(shown, past.length ? past[past.length - 1].value : null), exact: false };
	}, [node.id, node.formula, node.observed, shown, dragging, detail.data]);
}

/** The market ladder for charts: repaired rungs and the monthly curve, from `GET /nodes/:h`. */
export function useMarket(node: EventNode) {
	const detail = useNodeDetail(node.market ? node.id : null);
	return detail.data?.market ?? null;
}
